/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import groupBy from 'lodash/groupBy';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import noop from 'lodash/noop';

import {useEvent, useOnChange, useOnMount} from '@edna/hooks';
import {mergeRefs} from '@edna/utils';

import {formField, newFormField} from '../FormField';
import Overlay from './Overlay';
import * as S from './style';
import {TCompareOption, TControlState, TProps, TSelectOption, TValue} from './types';
import {
  getOptions,
  getSelectedOptions,
  getValue,
  getValuesFromOptions,
  writableProps,
} from './utils';

export * from './types';
export {setWritableProps} from './utils';

const useDefaultValue = ({multiple, value: propValue, defaultValue: propDefaultValue}: TProps) => {
  const [value, setValue] = React.useState<TValue>(getValue(propValue, multiple));
  const defaultValue = React.useMemo<TValue>(() => {
    if (propDefaultValue) {
      return propDefaultValue;
    }

    return multiple ? [] : null;
  }, [multiple, propDefaultValue]);

  React.useEffect(() => {
    setValue((prevValue: TValue) => {
      if (propValue !== prevValue) {
        return propValue ?? defaultValue;
      }

      return prevValue;
    });
  }, [propValue, defaultValue]);

  return {value, setValue};
};

const useSelectedValue = (
  value: TProps['value'],
  propOptions: TSelectOption[],
  compareOption: TCompareOption,
) => {
  const selectedOptions: TSelectOption[] = React.useMemo(
    () => getSelectedOptions(value, compareOption, propOptions),
    [compareOption, propOptions, value],
  );

  const selectedValues = React.useMemo(
    () => getValuesFromOptions(selectedOptions),
    [selectedOptions],
  );

  return {selectedOptions, selectedValues};
};

type TValueProps = {
  multiple?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  option: TSelectOption;
  onChange?: (payload: TSelectOption) => void;
};

export const Value = React.memo<TValueProps>(
  ({multiple, option, disabled, readOnly, onChange = noop}) => {
    const handleDeleteOption = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!disabled && !readOnly) {
          event.stopPropagation();
          onChange(option);
        }
      },
      [disabled, onChange, option, readOnly],
    );

    const {disabled: optionDisabled, ...restOption} = option;

    if (multiple) {
      return (
        <S.Value multiple disabled={disabled || optionDisabled}>
          <S.ValueLabel multiple {...restOption}>
            {option.label}
          </S.ValueLabel>
          <S.DeleteButton
            type="button"
            disabled={disabled || readOnly || optionDisabled}
            onClick={handleDeleteOption}
          >
            <S.DeleteIcon />
          </S.DeleteButton>
        </S.Value>
      );
    }

    return (
      <S.Value disabled={disabled || optionDisabled}>
        <S.ValueLabel {...option}>{option.label}</S.ValueLabel>
      </S.Value>
    );
  },
);

const Select = React.memo<TProps>((props) => {
  const {
    onChange = noop,
    onFocus = noop,
    onBlur = noop,
    placementY = 'bottom',
    emptyLabel = writableProps.emptyLabel,
    compareOption = writableProps.compareOption,
    isDisplayEmpty = writableProps.isDisplayEmpty,
    options: propOptions = writableProps.options,
    multiple = false,
    className,
    isInvalid,
    placeholder,
    renderLabel,
    readOnly,
    disabled,
    innerRef = {current: null},
    autoFocus = false,
    closeOnMultiSelect = false,
    selector,
    headComponent: HeadComponent = S.Head,
    id,
    isLabelledBy,
  } = props;
  const isPlacementBottom = placementY === 'bottom';

  const [isOpen, setIsOpen] = React.useState(false);
  const [focusIndex, setFocusIndex] = React.useState(-1);
  const [parentFocusIndex, setParentFocusIndex] = React.useState(-1);

  const toggleIsOpen = React.useCallback(() => setIsOpen((prevState) => !prevState), []);

  const controlStateRef = React.useRef<TControlState>({isFocused: false});
  const headRef = React.useRef<HTMLDivElement>(null);
  const overlayRef = React.useRef<HTMLDivElement>(null);

  const {value, setValue} = useDefaultValue(props);
  const propOptionsWithEmpty = React.useMemo(
    () => getOptions(propOptions, {isDisplayEmpty, emptyLabel, multiple}),
    [propOptions, isDisplayEmpty, emptyLabel, multiple],
  );
  const [options, setOptions] = React.useState<TSelectOption[]>(propOptionsWithEmpty);
  const [selectedOption, setSelectedOption] = React.useState<TSelectOption | null>(null);

  const currentOptions = React.useMemo(() => {
    return selectedOption?.children ? selectedOption.children : options;
  }, [options, selectedOption]);

  const {selectedOptions, selectedValues} = useSelectedValue(
    value,
    propOptionsWithEmpty,
    compareOption,
  );

  useOnMount(() => {
    if (autoFocus) {
      const delay = isObject(autoFocus) && autoFocus?.delay ? autoFocus.delay : 0;

      setTimeout(() => {
        setIsOpen(true);
      }, delay);
    }
  });

  useOnChange(() => {
    if (!isOpen) {
      setFocusIndex(-1);
      setParentFocusIndex(-1);
      setSelectedOption(null);
    }
  }, [isOpen]);

  const handleChange = React.useCallback(
    (nextValue: TValue) => {
      onChange(nextValue);

      if (!multiple || closeOnMultiSelect) {
        setIsOpen(false);
      }
    },
    [onChange, multiple],
  );

  const handleFocus = React.useCallback<React.FocusEventHandler<HTMLDivElement>>(
    (event) => {
      controlStateRef.current.isFocused = true;
      onFocus(event);
    },
    [onFocus],
  );

  const handleBlur = React.useCallback<React.FocusEventHandler<HTMLDivElement>>(
    (event) => {
      controlStateRef.current.isFocused = false;
      onBlur(event);
    },
    [onBlur],
  );

  const handleKeydown = React.useCallback(
    // eslint-disable-next-line complexity
    (event: KeyboardEvent) => {
      switch (event.code) {
        case 'Escape':
          if (isOpen) {
            toggleIsOpen();

            return;
          }
          break;

        case 'Space':
          if (controlStateRef.current.isFocused) {
            event.preventDefault();
            toggleIsOpen();

            return;
          }
          break;

        case 'Enter':
          const option = options[focusIndex];

          if (controlStateRef.current.isFocused && !option?.children) {
            // открываем overlay при нажатии enter
            toggleIsOpen();
          }

          if (isOpen) {
            event.preventDefault();
            event.stopPropagation();

            if (option?.children) {
              setOptions(option.children);
              setSelectedOption(option);
              setParentFocusIndex(focusIndex);
              setFocusIndex(0);

              return;
            }

            const isSelected = selectedValues.some((selectedValue) =>
              isEqual(selectedValue, option.value),
            );

            if (!isSelected) {
              handleChange(multiple ? [...selectedValues, option.value] : option.value);
            }
          }
          break;

        case 'ArrowUp':
          if (isOpen) {
            setFocusIndex(Math.max(0, focusIndex - 1));
          }
          break;

        case 'ArrowDown':
          if (isOpen) {
            setFocusIndex(Math.min(options.length - 1, focusIndex + 1));
          }
          break;

        case 'ArrowLeft':
          if (isOpen && selectedOption) {
            setSelectedOption(null);
            setOptions(propOptionsWithEmpty);
            setFocusIndex(parentFocusIndex);
            setParentFocusIndex(-1);
          }
          break;
      }
    },
    [
      isOpen,
      toggleIsOpen,
      setFocusIndex,
      focusIndex,
      options,
      selectedValues,
      handleChange,
      multiple,
    ],
  );

  const handleClick = React.useCallback(
    (event: MouseEvent) => {
      if (isOpen && !overlayRef.current?.contains(event.target as Node)) {
        controlStateRef.current.isFocused = false;
        setIsOpen(false);
      }
    },
    [isOpen],
  );

  useEvent('keydown', handleKeydown);
  useEvent('click', handleClick);

  const handleDeleteOption = React.useCallback(
    (option: TSelectOption) => {
      if (multiple) {
        onChange(selectedValues.filter((selectedValue) => !compareOption(option, selectedValue)));
      }
    },
    [compareOption, multiple, onChange, selectedValues],
  );

  const renderValues = () => {
    if (selectedOptions.length === 0) {
      return (
        <S.Placeholder disabled={disabled} readOnly={readOnly}>
          {placeholder}
        </S.Placeholder>
      );
    }

    const renderValue = (option: TSelectOption) => {
      if (renderLabel) {
        return renderLabel({
          option,
          isHead: true,
          disabled,
          readOnly,
          multiple,
          onChange: handleDeleteOption,
        });
      }

      return (
        <Value
          key={JSON.stringify(option.value)}
          option={option}
          disabled={disabled}
          readOnly={readOnly}
          multiple={multiple}
          onChange={handleDeleteOption}
        />
      );
    };

    const shouldGroupOptions = selectedOptions.some((option) => option.parentValue);

    if (!shouldGroupOptions) {
      return selectedOptions.map(renderValue);
    }

    const groupedOptions = groupBy(selectedOptions, (option) =>
      isObject(option.parentValue) ? JSON.stringify(option.parentValue) : option.parentValue || '',
    );

    return Object.entries(groupedOptions).map(([parentValue, optionsList]) => (
      <div key={parentValue || 'ungrouped options'}>
        {optionsList[0].parentLabel && <S.ParentLabel>{optionsList[0].parentLabel}</S.ParentLabel>}
        {optionsList.map(renderValue)}
      </div>
    ));
  };

  return (
    <S.Select className={className} data-selector={selector}>
      <HeadComponent
        multiple={multiple}
        isOpen={isOpen}
        isPlacementBottom={isPlacementBottom}
        isInvalid={isInvalid}
        disabled={disabled}
        readOnly={readOnly}
        onClick={disabled || readOnly ? undefined : toggleIsOpen}
        onFocus={disabled ? undefined : handleFocus}
        onBlur={disabled ? undefined : handleBlur}
        tabIndex={-1}
        ref={mergeRefs(innerRef, headRef)}
      >
        {renderValues()}
        <input
          type="hidden"
          id={isLabelledBy ? undefined : id}
          aria-labelledby={isLabelledBy ? id : undefined}
          placeholder={String(placeholder)}
        />
        <S.ArrowIconWrapper>
          <S.ArrowIcon open={isOpen} />
        </S.ArrowIconWrapper>
      </HeadComponent>
      <Overlay
        {...props}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        options={currentOptions}
        setOptions={setOptions}
        overlayRef={overlayRef}
        isOpen={isOpen}
        setValue={setValue}
        selectedOptions={selectedOptions}
        selectedValues={selectedValues}
        isPlacementBottom={isPlacementBottom}
        propOptions={propOptionsWithEmpty}
        onChange={handleChange}
        focusIndex={focusIndex}
        setFocusIndex={setFocusIndex}
      />
    </S.Select>
  );
});

export default Select;

export const SelectField = formField<TProps, TValue>(Select);

export const NewSelectField = newFormField<TProps>(Select, {isLabelledBy: true});
/* eslint-enable react/jsx-props-no-spreading */
